// Shared inline icon set (stroke-based, currentColor). Keeps the redesign
// self-contained — no external icon dependency.

export type IconProps = { className?: string };

const svg = (path: React.ReactNode) => {
  const Icon = ({ className = "h-5 w-5" }: IconProps) => (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
  return Icon;
};

export const ShieldIcon = svg(
  <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />,
);

export const ShieldCheckIcon = svg(
  <>
    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </>,
);

export const CheckIcon = svg(<path d="M5 13l4 4L19 7" />);

export const CheckCircleIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </>,
);

export const ClockIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);

export const UserIcon = svg(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
  </>,
);

export const UsersIcon = svg(
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2 21v-1a5.5 5.5 0 0 1 5.5-5.5h3A5.5 5.5 0 0 1 16 20v1" />
    <path d="M17 4.5a3.5 3.5 0 0 1 0 7" />
    <path d="M18 14.5a5.5 5.5 0 0 1 4 5.5v1" />
  </>,
);

export const InfoIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </>,
);

export const ChevronDownIcon = svg(<path d="M6 9l6 6 6-6" />);

export const GlobeIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
  </>,
);

export const PhoneIcon = svg(
  <path d="M5 4h3l2 5-2.5 1.5a11 11 0 005 5L19 13l2 5v3a1 1 0 01-1 1A16 16 0 014 5a1 1 0 011-1z" />,
);

export const LinkIcon = svg(
  <>
    <path d="M9 15l6-6" />
    <path d="M11 6l1-1a4 4 0 015.66 5.66l-2 2" />
    <path d="M13 18l-1 1a4 4 0 01-5.66-5.66l2-2" />
  </>,
);

export const MapPinIcon = svg(
  <>
    <path d="M12 21c-4-4-7-7.5-7-11a7 7 0 1114 0c0 3.5-3 7-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </>,
);

export const CalendarIcon = svg(
  <>
    <rect x="4" y="5" width="16" height="16" rx="2" />
    <path d="M4 9h16M8 3v4M16 3v4" />
  </>,
);

export const CalendarCheckIcon = svg(
  <>
    <rect x="4" y="5" width="16" height="16" rx="2" />
    <path d="M4 9h16M8 3v4M16 3v4M9 15l2 2 4-4" />
  </>,
);

export const ActivityIcon = svg(<path d="M3 12h4l2 6 4-14 2 8h6" />);

export const BriefcaseMedicalIcon = svg(
  <>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M12 11v5M9.5 13.5h5" />
  </>,
);

export const LockIcon = svg(
  <>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </>,
);

export const BuildingIcon = svg(
  <>
    <rect x="5" y="3" width="14" height="18" rx="1" />
    <path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1M10 21v-3h4v3" />
  </>,
);

export const StarIcon = svg(
  <path d="M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8L12 4z" />,
);

export const ArrowRightIcon = svg(<path d="M5 12h14M13 6l6 6-6 6" />);

export const ExternalLinkIcon = svg(
  <>
    <path d="M14 4h6v6M20 4l-9 9" />
    <path d="M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
  </>,
);

export const SearchIcon = svg(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </>,
);

export const FingerprintIcon = svg(
  <>
    <path d="M4 12a8 8 0 0 1 14-5.3" />
    <path d="M5.5 18.5C6.5 16.7 7 14.4 7 12a5 5 0 0 1 9.5-2.2" />
    <path d="M9.5 20.5C10.5 18.4 11 15.3 11 12a1 1 0 0 1 2 0c0 3.5-.5 6.5-1.5 9" />
    <path d="M14.5 12c0 2.5-.3 5-1 7.3" />
  </>,
);

export const DocumentIcon = svg(
  <>
    <path d="M6 3h8l4 4v14a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" />
    <path d="M14 3v4h4" />
  </>,
);
